// src/lib/forms/consentFields.ts
//
// Task 1.5 of docs/builder-fields-and-errors_plan.md. One shared definition of
// the consent marker so the builder's palette, `FormField.metadata`, the
// validator (task 1.6) and the Brevo panel (task 1.7) read the same key and
// the same two kinds, rather than each inventing its own spelling the way
// `TRACKING_CONSENT_REASONS` and `consentSourceNames` once drifted apart
// (.wolf/cerebrum.md).
//
// No new FieldType: a consent field is an ordinary `checkbox` field, marked
// by this one metadata key. `src/lib/database/forms.ts` round-trips the key
// through `FormField.metadata` (a Json column that already carries
// `defaultValue`, `className`, `helpText` and `logic` the same way) -- no
// migration.
//
// `safeUrl` is imported by relative path, not `@/lib/utils/safeUrl`, because
// this module is also imported by `src/scripts/kiroku-embed.ts` (task 1.2,
// docs/consent-link-and-retention-notice_plan.md) and bundled by
// `scripts/build-embed.mjs`'s esbuild, which has no `@/` alias configured
// (only the react->preact/compat alias) -- a bare `@/` specifier there fails
// the build rather than the type-check, since esbuild resolves imports at
// bundle time regardless of whether TypeScript would.
import { safeNavigationUrl } from "../utils/safeUrl";

/** The `FormField.metadata` key that marks a consent checkbox. */
export const CONSENT_KIND_METADATA_KEY = "consentKind";

/**
 * The `FormField.metadata` key for the optional privacy-policy link shown
 * after a consent checkbox's wording (task 1.1,
 * docs/consent-link-and-retention-notice_plan.md). Only meaningful on a
 * field that also carries `CONSENT_KIND_METADATA_KEY`.
 */
export const CONSENT_PRIVACY_URL_METADATA_KEY = "consentPrivacyUrl";

/**
 * "marketing" ships in tasks 1.5-1.7 (optional, preselected by the Brevo
 * panel). "terms" ships in task 1.8 (required, never preselected). Both are
 * declared here together so a later task cannot introduce a third spelling
 * independently of this file.
 */
export type ConsentKind = "marketing" | "terms";
export const CONSENT_KINDS: readonly ConsentKind[] = ["marketing", "terms"];

export interface ConsentFieldOption {
  value: string;
  label: string;
}

export interface ConsentFieldPreset {
  consentKind: ConsentKind;
  /** The field's label, shown above the checkbox. */
  label: string;
  /** The single option a consent checkbox carries -- its wording is the agreement text itself. */
  option: ConsentFieldOption;
  required: boolean;
  /** The palette entry's own label and tooltip, in the plain words CLAUDE.md section 0a asks for on every dashboard surface. */
  paletteLabel: string;
  paletteTooltip: string;
  paletteIcon: string;
}

/**
 * Task 1.5: a one-click optional opt-in, always unticked (never a default
 * value), that the Brevo panel preselects as its consent field (task 1.7)
 * when nothing has been saved yet.
 */
export const MARKETING_OPT_IN_PRESET: ConsentFieldPreset = {
  consentKind: "marketing",
  label: "Marketing opt-in",
  option: {
    value: "yes",
    label: "Yes, I would like to receive news and offers by email.",
  },
  required: false,
  paletteLabel: "Marketing opt-in",
  paletteTooltip:
    "A box people can tick to say yes to emails from you. It starts unticked and people can leave it empty.",
  paletteIcon: "mail-check",
};

/**
 * Task 1.8: a one-click required agreement, also always unticked --
 * `describeConsentFieldViolation` below forbids a pre-ticked box for either
 * kind, and `required: true` is what makes this one different from the
 * marketing preset rather than optional like it. Never preselected by the
 * Brevo panel (task 1.7 checks for `MARKETING_OPT_IN_PRESET.consentKind`
 * specifically).
 */
export const TERMS_AGREEMENT_PRESET: ConsentFieldPreset = {
  consentKind: "terms",
  label: "Terms agreement",
  option: {
    value: "agree",
    label: "I agree to the terms and privacy policy.",
  },
  required: true,
  paletteLabel: "Terms agreement",
  paletteTooltip:
    "A box people must tick before they can send the form, saying they agree to your terms and privacy policy.",
  paletteIcon: "shield-check",
};

/**
 * True for any `defaultValue` that would show the checkbox pre-ticked --
 * `true`, a non-empty string, a non-zero number, or a non-empty array -- the
 * same "is this on" reading `FieldEditor.tsx`'s own default-value checkbox
 * input gives a `boolean`, widened to the other shapes
 * `validateFormDefinitionPayload` already accepts for `defaultValue`.
 */
function isDefaultValuePreTicked(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value);
}

/**
 * Task 1.6 of docs/builder-fields-and-errors_plan.md: a consent checkbox must
 * never reach a visitor pre-ticked (neither kind -- silent agreement is not
 * consent under the laws PowerfulForm's documentation cites,
 * docs/marketing-opt-in_research.md), and a marketing opt-in specifically
 * must never be required, because the whole point is that saying no costs
 * nothing. A terms-agreement field (task 1.8) may be required.
 *
 * Returns the plain-words message to show the owner, or `null` when the
 * field is fine -- including every field with no `consentKind` at all, which
 * this rule never touches.
 */
export function describeConsentFieldViolation(field: {
  consentKind?: ConsentKind | null;
  defaultValue?: unknown;
  required?: boolean | null;
}): string | null {
  if (!field.consentKind) return null;

  if (isDefaultValuePreTicked(field.defaultValue)) {
    return field.consentKind === "marketing"
      ? "A marketing opt-in cannot start ticked."
      : "A terms agreement cannot start ticked.";
  }

  if (field.consentKind === "marketing" && field.required) {
    return "A marketing opt-in cannot be required, because people must be free to say no.";
  }

  return null;
}

/** The link text every renderer draws after a consent checkbox's wording. */
export const CONSENT_PRIVACY_LINK_TEXT = "Privacy policy";

/**
 * Shown under a consent field's controls in the builder, and as a paragraph
 * on the data retention settings page (task 2.1,
 * docs/consent-link-and-retention-notice_plan.md). A consent answer is
 * stored as a `FieldValue` row on its submission, so an owner's retention
 * setting deletes it along with everything else, which is a surprise for
 * anyone relying on the box as proof of agreement. One constant so the
 * builder and the settings page cannot drift onto two different sentences.
 */
export const CONSENT_RETENTION_NOTICE =
  "When old submissions are deleted, the answers to this box are deleted with them. Keep your own record if you need proof that someone agreed.";

/**
 * The retention notice, with the account's own submission retention period
 * appended when one is set. Pulled into its own function (rather than left
 * for each caller to assemble) so the data retention settings page's half of
 * task 2.1 is a single unit the guard test can check directly: Astro's
 * container API needs its own vitest project
 * (`tests/astro/**`, see `vitest.workspace.ts`) and cannot be loaded from
 * `tests/guard/`, so a plain function here is what makes the logic testable
 * from a guard test without rendering the `.astro` file.
 */
export function consentRetentionNoticeWithPeriod(
  submissionRetentionDays: number | null,
): string {
  if (!submissionRetentionDays) return CONSENT_RETENTION_NOTICE;
  return `${CONSENT_RETENTION_NOTICE} Right now submissions are kept for ${submissionRetentionDays} days.`;
}

/**
 * The privacy-policy address a consent checkbox should link to, or `null`
 * when there is none safe to show (task 1.2,
 * docs/consent-link-and-retention-notice_plan.md).
 *
 * Never meaningful on a field with no `consentKind` -- an ordinary checkbox
 * has no wording to point a policy at. The field's own address
 * (`consentPrivacyUrl`) wins; the form-wide one (`settings.privacyPolicyUrl`)
 * is the fallback, so an owner who already set that does not have to enter it
 * twice. Both go through `safeNavigationUrl` here, at render time, rather
 * than trusting whatever passed validation at save time: a value stored
 * before this plan shipped, or edited directly in the database, must still be
 * refused on every surface that draws it.
 */
export function resolveConsentPrivacyLink(
  field: {
    consentKind?: ConsentKind | null;
    consentPrivacyUrl?: string | null;
  },
  formPrivacyPolicyUrl?: string | null,
): string | null {
  if (!field.consentKind) return null;
  return (
    safeNavigationUrl(field.consentPrivacyUrl, { allowRelative: true }) ??
    safeNavigationUrl(formPrivacyPolicyUrl, { allowRelative: true })
  );
}
